import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Box, Container, GridLegacy as Grid, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { retrievePNRBookingDetails } from '~/services/FlightsService';

import { reportError } from '~/utils/reportError';

import { withTenant } from '../../hoc';

import OrderFlightLeg from './BookingDetails/OrderFlightLeg';
import OrderTravellerDetails from './BookingDetails/OrderTravellerDetails';
import PnrJsonViewer from './BookingDetails/PnrJsonViewer';

function OrderBookingPage({ order, customer }) {
  const { id_orders: orderId } = useParams<{ id_orders: string }>();

  const [provider, setProvider] = useState('');
  const [region, setRegion] = useState(null);
  const [pnrId, setPnrId] = useState(null);
  const [travellers, setTravellers] = useState([]);
  const [flights, setFlights] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bookingRef, setBookingRef] = useState('');
  const [sfpd, setSfpd] = useState('');
  const [routeType, setRouteType] = useState('');

  const constructTravellerDetails = (travellerInfo) => {
    return travellerInfo.map((traveller) => {
      const enhancedInfo = traveller?.enhancedPassengerData?.[0]?.enhancedTravellerInformation?.[0] || {};
      const { otherPaxNamesDetails = [], travellerNameInfo = [] } = enhancedInfo;

      const title = otherPaxNamesDetails?.[0]?.title?.[0];
      const givenName = otherPaxNamesDetails?.[0]?.givenName?.[0];
      const surname = otherPaxNamesDetails?.[0]?.surname?.[0];
      const type = travellerNameInfo?.[0]?.type?.[0];

      const miscFiels = {
        ticketNo: 'TBD',
        gross: 0,
        taxes: 0,
        net: 0,
        fees: 0,
        baggage: 'TBD',
      };
      return { ...miscFiels, title, givenName, surname, type };
    });
  };

  type FlightDetails = {
    departureLocation: string;
    departureDate: string;
    departureTime: string;
    arrivalLocation: string;
    arrivalDate: string;
    arrivalTime: string;
    aircraftNumber: string;
  };

  const constructFlightDetails = (originDestinationDetails): Array<FlightDetails> => {
    const flights = [];
    originDestinationDetails?.[0]?.itineraryInfo.forEach((itinerary, _itineraryIdx) => {
      const { travelProduct, legInfo } = itinerary;
      const { companyDetail, productDetails } = travelProduct[0];

      legInfo.forEach((leg) => {
        const { flightDate, boardPointDetails, offpointDetails } = leg.legTravelProduct[0];
        const { departureDate, departureTime, arrivalDate, arrivalTime } = flightDate[0];

        const flightDetails = {
          departureLocation: boardPointDetails[0].trueLocationId[0],
          departureDate: departureDate[0],
          departureTime: departureTime[0],
          arrivalLocation: offpointDetails[0].trueLocationId[0],
          arrivalDate: arrivalDate[0],
          arrivalTime: arrivalTime[0],
          aircraftNumber: `${companyDetail[0].identification}${productDetails[0].identification[0]}`,
        };
        flights.push(flightDetails);
      });
    });
    return flights;
  };

  const constructPaymentDetails = (tstData) => {
    const payments = [];
    tstData?.forEach((tst) => {
      const { fareData } = tst;
      const { monetaryInfo } = fareData[0];

      monetaryInfo.forEach((monetary) => {
        if (monetary.qualifier[0] === 'T') {
          const paymentDetails = {
            currency: monetary.currencyCode[0],
            amount: monetary.amount[0],
            transactionDate: 'TBD',
            method: 'TBD',
            payment_id: 'TBD',
          };
          payments.push(paymentDetails);
        }
      });
    });
    return payments;
  };

  useEffect(() => {
    const loadBookingDetails = async () => {
      const { region_code: orderRegion } = order;
      const flightItem = order?.flight_items?.[0] || {};
      const { provider: flightProvider = '', pnr_id: flightPnrId = null } = flightItem;

      setProvider(flightProvider);
      setRegion(orderRegion);
      setPnrId(flightPnrId);

      try {
        const pnrResponse = await retrievePNRBookingDetails({
          pnrId: flightPnrId,
          region: orderRegion,
          provider: flightProvider,
        });
        const { travellerInfo, originDestinationDetails, tstData } = pnrResponse.result;

        const travellerDetails = constructTravellerDetails(travellerInfo);
        const flightDetails = constructFlightDetails(originDestinationDetails);
        const paymentDetails = constructPaymentDetails(tstData);

        setTravellers(travellerDetails);
        setFlights(flightDetails);
        setPayments(paymentDetails);
        setBookingRef('TBD');
        setSfpd('TBD');
        setRouteType('TBD');
      } catch (error) {
        reportError(error);
      }
    };

    loadBookingDetails();
  }, [order]);

  return (
    <Container maxWidth="xl" className="order-detail" data-no-dark-override>
      <Helmet>
        <title>Orders | {customer.fullName} | Order Booking Details</title>
      </Helmet>

      <Box id="timeline">
        <PageHeader title="Booking details" backButton={`/purchases/${orderId}`} />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="overline" color="text.secondary" display="block">
              LE Booking Reference
            </Typography>
            <Typography variant="body1">{bookingRef}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="overline" color="text.secondary" display="block">
              SFPD
            </Typography>
            <Typography variant="body1">{sfpd}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="overline" color="text.secondary" display="block">
              Route Type
            </Typography>
            <Typography variant="body1">{routeType}</Typography>
          </Grid>
        </Grid>

        <Box className="detail-container items-panel-container" sx={{ mb: 4 }}>
          <Box className="offer-details">
            <Box>
              <Box>
                <Typography variant="h6" className="order-header">
                  Departing / Returning Flights
                </Typography>
              </Box>
            </Box>
            <Box>
              <OrderFlightLeg flights={flights} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Traveller Details
          </Typography>
          <Box>
            <OrderTravellerDetails travellers={travellers} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Payment Details
        </Typography>
        <Box>
          <DataGrid
            columns={[
              { field: 'method', headerName: 'Method', width: 150, display: 'flex' },
              { field: 'payment_id', headerName: 'Reference', width: 150, display: 'flex' },
              { field: 'currency', headerName: 'Currency', width: 150, display: 'flex' },
              { field: 'transactionDate', headerName: 'Transaction Date', width: 150, display: 'flex' },
              { field: 'amount', headerName: 'Amount', width: 150, display: 'flex' },
            ]}
            rows={payments}
            getRowId={(row) => row.payment_id || row.givenName + row.surname + row.type}
            autoHeight
            disableColumnFilter
            disableColumnMenu
            disableRowSelectionOnClick
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Passenger Name Record (PNR) Record
        </Typography>
        <Box>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <Typography variant="overline" color="text.secondary" display="block">
                Region
              </Typography>
              <Typography variant="body1">{region}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="overline" color="text.secondary" display="block">
                Provider
              </Typography>
              <Typography variant="body1">{provider.toUpperCase()}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="overline" color="text.secondary" display="block">
                PNR ID
              </Typography>
              <Typography variant="body1">{pnrId}</Typography>
            </Grid>
          </Grid>
          <PnrJsonViewer pnrId={pnrId} region={region} provider={provider} />
        </Box>
      </Box>
    </Container>
  );
}

OrderBookingPage.defaultProps = {};

// Exported without router & redux HOC for testing
export const OrderDetailPageComponent = OrderBookingPage;

export default withTenant(OrderBookingPage);
